/**
 * Ticket sale phases — bind only to backend fields:
 * phasedPricingEnabled, phases[{name,quantity,price}], totaltickets, ticketprice, ticketssold.
 */

export function asPositiveInt(value, fallback = 0) {
  const n = parseInt(String(value ?? "").trim(), 10);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
}

export function asNonNegativeNumber(value, fallback = 0) {
  const n = Number(String(value ?? "").trim());
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
}

export function emptyPhase(index = 0) {
  return {
    name: `Phase ${index + 1}`,
    quantity: 1,
    price: 0,
  };
}

export function normalizePhase(raw, index = 0) {
  return {
    name: String(raw?.name ?? `Phase ${index + 1}`).trim() || `Phase ${index + 1}`,
    quantity: Math.max(1, asPositiveInt(raw?.quantity, 1)),
    price: asNonNegativeNumber(raw?.price, 0),
  };
}

export function normalizePhases(list) {
  if (!Array.isArray(list)) return [];
  return list.map((p, i) => normalizePhase(p, i));
}

export function isPhasedPricingEnabled(ticket) {
  return Boolean(ticket?.phasedPricingEnabled);
}

export function sumPhaseQuantity(phases) {
  return (phases || []).reduce(
    (acc, p) => acc + Math.max(0, asPositiveInt(p?.quantity, 0)),
    0
  );
}

/**
 * Prefer API `phaseStatus` when present; otherwise derive from ticketssold + phases.
 */
export function resolvePhaseStatus(ticket) {
  const api = ticket?.phaseStatus;
  if (api && typeof api === "object") {
    return {
      currentPhaseIndex: api.currentPhaseIndex ?? 0,
      currentPhase: api.currentPhase ?? null,
      currentPrice:
        api.currentPrice ??
        ticket?.effectiveTicketPrice ??
        ticket?.ticketprice ??
        0,
      remainingInPhase: api.remainingInPhase ?? 0,
      remainingTotal: api.remainingTotal ?? 0,
      phaseSoldOut: Boolean(api.phaseSoldOut),
      allPhasesSoldOut: Boolean(api.allPhasesSoldOut),
      phases: Array.isArray(api.phases) ? api.phases : ticket?.phases || [],
    };
  }

  const phases = normalizePhases(ticket?.phases);
  const sold = asPositiveInt(ticket?.ticketssold, 0);
  const total = sumPhaseQuantity(phases);
  let cursor = 0;
  const derived = phases.map((phase, index) => {
    const start = cursor;
    const qty = phase.quantity;
    const end = start + qty;
    const soldInPhase = Math.min(qty, Math.max(0, sold - start));
    const remaining = qty - soldInPhase;
    const isPast = sold >= end;
    const isCurrent = sold >= start && sold < end;
    cursor = end;
    return {
      ...phase,
      index,
      soldInPhase,
      remaining,
      isPast,
      isCurrent,
      isFuture: sold < start,
    };
  });

  const live =
    derived.find((p) => p.isCurrent) || derived.find((p) => p.remaining > 0);
  const currentPhaseIndex = live ? live.index : phases.length ? phases.length - 1 : 0;
  const remainingInPhase = live ? live.remaining : 0;

  const allSold = phases.length > 0 && sold >= total;
  const current = derived[currentPhaseIndex] || null;
  const remainingTotal = Math.max(0, total - sold);

  return {
    currentPhaseIndex,
    currentPhase: current
      ? { name: current.name, quantity: current.quantity, price: current.price }
      : null,
    currentPrice: current?.price ?? ticket?.effectiveTicketPrice ?? ticket?.ticketprice ?? 0,
    remainingInPhase: allSold ? 0 : remainingInPhase,
    remainingTotal,
    phaseSoldOut: remainingInPhase <= 0 && !allSold,
    allPhasesSoldOut: allSold || Boolean(ticket?.soldout),
    phases: derived,
  };
}

export function isPhaseLocked(ticket, phaseIndex) {
  const sold = asPositiveInt(ticket?.ticketssold, 0);
  if (sold <= 0) return false;
  const phases = normalizePhases(ticket?.phases);
  let cursor = 0;
  for (let i = 0; i < phases.length; i += 1) {
    const end = cursor + phases[i].quantity;
    if (i === phaseIndex) {
      return sold >= end;
    }
    cursor = end;
  }
  return false;
}

export function validateTicketPhases(ticket, t) {
  if (!isPhasedPricingEnabled(ticket)) return null;
  const phases = Array.isArray(ticket?.phases) ? ticket.phases : [];
  if (phases.length < 1) {
    return t("EVENT_PHASES_ERR_MIN_ONE");
  }
  for (let i = 0; i < phases.length; i += 1) {
    const name = String(phases[i]?.name ?? "").trim();
    const qty = Number(phases[i]?.quantity);
    const price = Number(phases[i]?.price);
    if (!name) return t("EVENT_PHASES_ERR_NAME", { n: i + 1 });
    if (!Number.isFinite(qty) || qty < 1) {
      return t("EVENT_PHASES_ERR_QTY", { n: i + 1 });
    }
    if (!Number.isFinite(price) || price < 0) {
      return t("EVENT_PHASES_ERR_PRICE", { n: i + 1 });
    }
  }
  return null;
}

export function applyPhaseTotalsToTicket(ticket) {
  if (!isPhasedPricingEnabled(ticket)) {
    const { phases, ...rest } = ticket || {};
    return {
      ...rest,
      phasedPricingEnabled: false,
    };
  }
  const phases = normalizePhases(ticket?.phases);
  const status = resolvePhaseStatus({ ...ticket, phases });
  return {
    ...ticket,
    phasedPricingEnabled: true,
    phases,
    totaltickets: sumPhaseQuantity(phases),
    ticketprice: status.currentPrice,
  };
}

export function prepareTicketPhasesForApi(ticket) {
  const prepared = applyPhaseTotalsToTicket(ticket);
  if (!prepared.phasedPricingEnabled) {
    return {
      ...prepared,
      phasedPricingEnabled: false,
    };
  }
  return {
    ...prepared,
    phasedPricingEnabled: true,
    phases: (prepared.phases || []).map((p) => ({
      name: String(p.name).trim(),
      quantity: Math.max(1, asPositiveInt(p.quantity, 1)),
      price: asNonNegativeNumber(p.price, 0),
    })),
  };
}

export function formatInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

export function buyerMaxQty(ticket, hardCap = 10) {
  if (!isPhasedPricingEnabled(ticket)) {
    const remaining =
      asPositiveInt(ticket?.totaltickets, 0) -
      asPositiveInt(ticket?.ticketssold, 0);
    return Math.max(0, Math.min(hardCap, remaining));
  }
  const status = resolvePhaseStatus(ticket);
  if (status.allPhasesSoldOut) return 0;
  return Math.max(0, Math.min(hardCap, status.remainingInPhase));
}

export function buyerDisplayPrice(ticket) {
  if (ticket?.effectiveTicketPrice != null && ticket.effectiveTicketPrice !== "") {
    return Number(ticket.effectiveTicketPrice);
  }
  if (isPhasedPricingEnabled(ticket)) {
    return Number(resolvePhaseStatus(ticket).currentPrice) || 0;
  }
  return Number(ticket?.ticketprice) || 0;
}
